

const Computer_Vision = [
{
  "id": 1,
  "html_title": "intro",
  "coursename": "Introduction to Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Computer Vision?",
      "bootstrap_title_desc": "Computer Vision is a field of Artificial Intelligence that enables computers to see, understand, and interpret visual information from the real world such as images and videos. The goal of computer vision is to replicate human vision by teaching machines how to extract meaningful information from pixels.\n\nExamples include face recognition, object detection, medical image analysis, and self-driving cars."
    },
    {
      "bootstrap_title_heading": "Why Learn Computer Vision?",
      "bootstrap_title_desc": "- **High demand skill**: Used in AI, robotics, healthcare, security, and automation.\n- **Real-world applications**: Face unlock, OCR, traffic monitoring, surveillance.\n- **Works with AI & Deep Learning**: Backbone of modern AI systems.\n- **Creative & impactful**: Turns images and videos into intelligent systems."
    },
    {
      "bootstrap_title_heading": "How Computer Vision Works",
      "bootstrap_title_desc": "Computer vision works by processing images as numerical data. An image is broken down into pixels, and each pixel has intensity values. Algorithms analyze patterns in these pixel values to detect edges, shapes, textures, and objects.\n\nThe basic pipeline is:\n1. Input image or video\n2. Preprocessing (resize, grayscale, noise removal)\n3. Feature extraction\n4. Decision or prediction"
    },
    {
      "bootstrap_title_heading": "What is an Image in Computer Vision?",
      "bootstrap_title_desc": "In computer vision, an image is represented as a matrix of numbers. Each number represents the intensity of light at a particular pixel location.\n\n- Grayscale image → 2D matrix\n- Color image (RGB) → 3D matrix (Height × Width × Channels)"
    },
    {
      "bootstrap_title_heading": "Real-World Applications of Computer Vision",
      "bootstrap_title_desc": "- Face detection and recognition\n- Image classification (cats vs dogs)\n- Object detection in traffic cameras\n- Medical imaging (X-rays, MRI scans)\n- Handwritten text recognition\n- Augmented Reality (AR)"
    },
    {
      "bootstrap_title_heading": "Simple Computer Vision Example",
      "bootstrap_title_desc": "Below is a very simple example of reading and displaying an image using OpenCV:\n\n```python\nimport cv2\n\n# Read image from file\nimage = cv2.imread(\"sample.jpg\")\n\n# Display the image\ncv2.imshow(\"My First CV Image\", image)\n\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```\n\nThis example shows how a computer loads an image as pixel data and displays it on the screen."
    },
    {
      "bootstrap_title_heading": "Tools Used in This Course",
      "bootstrap_title_desc": "- **Python**: Primary programming language\n- **OpenCV**: Image and video processing library\n- **NumPy**: Numerical operations on images\n- **Matplotlib**: Visualization\n- **Deep Learning frameworks** (later): TensorFlow / PyTorch"
    },
    {
      "bootstrap_title_heading": " Exercise",
      "bootstrap_title_desc": "1. Install OpenCV using pip:\n\n```bash\npip install opencv-python\n```\n\n2. Download any image from the internet.\n3. Write a Python script to read and display the image using OpenCV.\n\n**Task**: Change the window title and try displaying a different image."
    }
  ]
},

{
  "id": 2,
  "html_title": "digital-image-basics",
  "coursename": "Digital Image Basics and Pixels",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is a Digital Image?",
      "bootstrap_title_desc": "A digital image is a numerical representation of a visual scene. In computer vision, an image is stored as a grid of small units called pixels. Each pixel contains intensity values that represent color or brightness information.\n\nComputers do not see images as pictures; they see them as numbers arranged in matrices."
    },
    {
      "bootstrap_title_heading": "Understanding Pixels",
      "bootstrap_title_desc": "A pixel (picture element) is the smallest unit of an image. Each pixel stores information about color or intensity at a specific location.\n\n- In grayscale images, a pixel has a single value.\n- In color images, a pixel usually has three values (Red, Green, Blue)."
    },
    {
      "bootstrap_title_heading": "Image Dimensions and Shape",
      "bootstrap_title_desc": "Image dimensions describe the size of an image and are represented as:\n\n- Height × Width (grayscale)\n- Height × Width × Channels (color)\n\nFor example:\n- (480, 640) → grayscale image\n- (480, 640, 3) → color image"
    },
    {
      "bootstrap_title_heading": "Pixel Intensity Values",
      "bootstrap_title_desc": "Pixel intensity defines how bright or dark a pixel is.\n\n- In an 8-bit image, pixel values range from 0 to 255.\n- 0 represents black\n- 255 represents white\n- Values in between represent shades of gray or color intensity"
    },
    {
      "bootstrap_title_heading": "Grayscale vs Color Images",
      "bootstrap_title_desc": "Grayscale images contain only intensity information, making them simpler and faster to process.\n\nColor images usually use the RGB color model, where each pixel has three values:\n- Red channel\n- Green channel\n- Blue channel"
    },
    {
      "bootstrap_title_heading": "Reading Image Properties Using OpenCV",
      "bootstrap_title_desc": "The following example demonstrates how to read an image and inspect its properties:\n\n```python\nimport cv2\n\nimage = cv2.imread(\"sample.jpg\")\n\nprint(\"Image shape:\", image.shape)\nprint(\"Image data type:\", image.dtype)\n```\n\nThis helps understand how the image is stored internally in memory."
    },
    {
      "bootstrap_title_heading": "Accessing and Modifying Pixel Values",
      "bootstrap_title_desc": "You can access and modify individual pixel values using array indexing:\n\n```python\n# Access pixel at row 100, column 50\npixel = image[100, 50]\nprint(pixel)\n\n# Modify pixel value\nimage[100, 50] = [0, 0, 255]  # Red color in BGR format\n```\n\nThis is useful for understanding how computers manipulate images at the pixel level."
    },
    {
      "bootstrap_title_heading": " Exercise",
      "bootstrap_title_desc": "1. Load an image using OpenCV.\n2. Print its shape and data type.\n3. Access any pixel and print its value.\n4. Modify a small region of the image with a different color and display the result.\n\n**Goal**: Understand how images are stored and manipulated as pixel data."
    }
  ]
},

{
  "id": 3,
  "html_title": "color-spaces",
  "coursename": "Color Spaces in Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is a Color Space?",
      "bootstrap_title_desc": "A color space is a way of representing colors using numerical values. In computer vision, different color spaces are used to extract useful information from images. Choosing the right color space can simplify image processing tasks and improve model performance."
    },
    {
      "bootstrap_title_heading": "RGB Color Space",
      "bootstrap_title_desc": "RGB stands for Red, Green, and Blue. Each pixel is represented by three values corresponding to the intensity of red, green, and blue.\n\nOpenCV uses **BGR format by default**, which means the channel order is Blue, Green, Red."
    },
    {
      "bootstrap_title_heading": "Grayscale Color Space",
      "bootstrap_title_desc": "Grayscale images contain only intensity information and no color. They are represented as a 2D matrix and are widely used in edge detection, thresholding, and feature extraction because they reduce computational complexity."
    },
    {
      "bootstrap_title_heading": "HSV Color Space",
      "bootstrap_title_desc": "HSV stands for Hue, Saturation, and Value.\n\n- **Hue**: Type of color\n- **Saturation**: Color intensity\n- **Value**: Brightness\n\nHSV is very useful for color-based object detection because it separates color from lighting conditions."
    },
    {
      "bootstrap_title_heading": "Why Use Different Color Spaces?",
      "bootstrap_title_desc": "- Grayscale simplifies image processing\n- HSV works better for color detection tasks\n- RGB is useful for display and visualization\n\nUsing the correct color space makes computer vision algorithms more robust and efficient."
    },
    {
      "bootstrap_title_heading": "Convert RGB Image to Grayscale",
      "bootstrap_title_desc": "Example of converting a color image to grayscale using OpenCV:\n\n```python\nimport cv2\n\nimage = cv2.imread(\"sample.jpg\")\ngray = cv2.cvtColor(image, cv2.COLOR_BGR2GRAY)\n\ncv2.imshow(\"Grayscale Image\", gray)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Convert BGR Image to HSV",
      "bootstrap_title_desc": "Example of converting a color image to HSV color space:\n\n```python\nimport cv2\n\nimage = cv2.imread(\"sample.jpg\")\nhsv = cv2.cvtColor(image, cv2.COLOR_BGR2HSV)\n\ncv2.imshow(\"HSV Image\", hsv)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```\n\nHSV is commonly used in tasks like color-based object tracking."
    },
    {
      "bootstrap_title_heading": "Visualizing Individual Color Channels",
      "bootstrap_title_desc": "You can split an image into its individual color channels to better understand how color information is stored:\n\n```python\nb, g, r = cv2.split(image)\n\ncv2.imshow(\"Blue Channel\", b)\ncv2.imshow(\"Green Channel\", g)\ncv2.imshow(\"Red Channel\", r)\n\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Exercise)",
      "bootstrap_title_desc": "1. Load a color image using OpenCV.\n2. Convert it to grayscale and HSV.\n3. Display each image in separate windows.\n4. Split the image into B, G, and R channels and visualize them.\n\n**Goal**: Understand how color information is represented and transformed in computer vision."
    }
  ]
},

{
  "id": 4,
  "html_title": "introduction-to-opencv",
  "coursename": "Introduction to OpenCV",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is OpenCV?",
      "bootstrap_title_desc": "OpenCV (Open Source Computer Vision Library) is a powerful open-source library used for real-time computer vision applications. It provides thousands of optimized algorithms for image processing, video analysis, object detection, and machine learning.\n\nOpenCV is widely used in industries such as robotics, surveillance, healthcare, and autonomous systems."
    },
    {
      "bootstrap_title_heading": "Why Use OpenCV?",
      "bootstrap_title_desc": "- Open-source and free to use\n- Fast and optimized for real-time processing\n- Supports multiple platforms (Windows, Linux, macOS)\n- Easy integration with Python and deep learning frameworks\n- Large community and extensive documentation"
    },
    {
      "bootstrap_title_heading": "Installing OpenCV",
      "bootstrap_title_desc": "To use OpenCV in Python, install it using pip:\n\n```bash\npip install opencv-python\n```\n\nFor additional features (contrib modules):\n\n```bash\npip install opencv-contrib-python\n```"
    },
    {
      "bootstrap_title_heading": "Importing OpenCV in Python",
      "bootstrap_title_desc": "After installation, OpenCV can be imported in Python using the `cv2` module:\n\n```python\nimport cv2\n\nprint(cv2.__version__)\n```"
    },
    {
      "bootstrap_title_heading": "Understanding OpenCV Image Format",
      "bootstrap_title_desc": "OpenCV reads images as NumPy arrays in BGR color format. Each image is stored as a matrix of pixel values, which allows fast numerical operations and transformations."
    },
    {
      "bootstrap_title_heading": "Reading and Displaying an Image",
      "bootstrap_title_desc": "Example of reading and displaying an image using OpenCV:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\n\ncv2.imshow(\"Original Image\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Understanding cv2.waitKey() and destroyAllWindows()",
      "bootstrap_title_desc": "`cv2.waitKey()` waits for a keyboard event, and `cv2.destroyAllWindows()` closes all OpenCV windows. These functions are necessary to properly display images."
    },
    {
      "bootstrap_title_heading": "OpenCV Data Types and Images",
      "bootstrap_title_desc": "Images loaded with OpenCV are stored as NumPy arrays with data type `uint8`. This means pixel values range from 0 to 255."
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Install OpenCV in your system.\n2. Import OpenCV and print its version.\n3. Load an image and display it.\n4. Change the window title and display another image.\n\n**Goal**: Become comfortable setting up and using OpenCV for basic image operations."
    }
  ]
},

{
  "id": 5,
  "html_title": "image-input-output",
  "coursename": "Image Input, Display, and Saving",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Image Input in OpenCV",
      "bootstrap_title_desc": "Image input refers to reading an image file from storage into a program so that it can be processed. OpenCV provides the `cv2.imread()` function to load images into memory as NumPy arrays.\n\nIf the image path is incorrect, OpenCV will return `None`."
    },
    {
      "bootstrap_title_heading": "Reading an Image using cv2.imread()",
      "bootstrap_title_desc": "Example of loading an image from disk:\n\n```python\nimport cv2\n\nimage = cv2.imread(\"image.jpg\")\n\nif image is None:\n    print(\"Image not found\")\nelse:\n    print(\"Image loaded successfully\")\n```"
    },
    {
      "bootstrap_title_heading": "Displaying Images using cv2.imshow()",
      "bootstrap_title_desc": "`cv2.imshow()` displays an image in a new window. It requires `cv2.waitKey()` to keep the window open until a key is pressed."
    },
    {
      "bootstrap_title_heading": "Display Multiple Images",
      "bootstrap_title_desc": "You can display multiple images in different windows:\n\n```python\ncv2.imshow(\"Original\", image)\ncv2.imshow(\"Duplicate\", image)\n\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Saving Images using cv2.imwrite()",
      "bootstrap_title_desc": "`cv2.imwrite()` saves an image to disk. This is useful after applying transformations or filters.\n\n```python\ncv2.imwrite(\"output.jpg\", image)\n```"
    },
    {
      "bootstrap_title_heading": "Reading Images in Different Modes",
      "bootstrap_title_desc": "OpenCV allows reading images in different modes:\n\n- Color image (default)\n- Grayscale image\n- Unchanged image\n\n```python\ncv2.imread(\"image.jpg\", cv2.IMREAD_GRAYSCALE)\n```"
    },
    {
      "bootstrap_title_heading": "Example: Convert and Save Grayscale Image",
      "bootstrap_title_desc": "This example converts a color image to grayscale and saves it:\n\n```python\ngray = cv2.cvtColor(image, cv2.COLOR_BGR2GRAY)\ncv2.imwrite(\"gray_image.jpg\", gray)\n```"
    },
    {
      "bootstrap_title_heading": "Common Errors and Best Practices",
      "bootstrap_title_desc": "- Always check if image is loaded successfully\n- Use correct file paths\n- Call `cv2.destroyAllWindows()` after displaying images\n- Use meaningful filenames when saving output"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load an image in color and grayscale.\n2. Display both images in separate windows.\n3. Save the grayscale image with a new name.\n4. Try loading an incorrect image path and observe the result.\n\n**Goal**: Master basic image input and output operations in OpenCV."
    }
  ]
},


{
  "id": 6,
  "html_title": "image-resizing-cropping",
  "coursename": "Image Resizing and Cropping",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Why Resize and Crop Images?",
      "bootstrap_title_desc": "Resizing and cropping are common preprocessing steps in computer vision. Resizing changes the image dimensions, while cropping extracts a specific region of interest (ROI) from an image.\n\nThese operations are important for standardizing image sizes, focusing on important areas, and reducing computational cost."
    },
    {
      "bootstrap_title_heading": "Image Resizing Basics",
      "bootstrap_title_desc": "Image resizing adjusts the width and height of an image. OpenCV provides the `cv2.resize()` function to resize images using different interpolation methods."
    },
    {
      "bootstrap_title_heading": "Resizing an Image to Fixed Dimensions",
      "bootstrap_title_desc": "Example of resizing an image to a fixed width and height:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\nresized = cv2.resize(img, (300, 300))\n\ncv2.imshow(\"Resized Image\", resized)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Maintaining Aspect Ratio While Resizing",
      "bootstrap_title_desc": "Maintaining the aspect ratio prevents image distortion. This is done by calculating the new height or width based on the original dimensions."
    },
    {
      "bootstrap_title_heading": "Interpolation Methods in Resizing",
      "bootstrap_title_desc": "Interpolation determines how pixel values are calculated when resizing:\n\n- `INTER_LINEAR` (default)\n- `INTER_CUBIC` (better quality)\n- `INTER_AREA` (best for downscaling)"
    },
    {
      "bootstrap_title_heading": "Cropping an Image",
      "bootstrap_title_desc": "Cropping extracts a portion of an image using array slicing. It is commonly used to focus on a specific object or region of interest."
    },
    {
      "bootstrap_title_heading": "Example: Cropping a Region of Interest (ROI)",
      "bootstrap_title_desc": "Example of cropping a specific region from an image:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\n\n# Crop: img[y1:y2, x1:x2]\ncropped = img[100:300, 150:350]\n\ncv2.imshow(\"Cropped Image\", cropped)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Use Case",
      "bootstrap_title_desc": "Resizing is commonly used to prepare images for deep learning models that require fixed input sizes. Cropping is used in face detection to extract faces from images."
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load an image.\n2. Resize it to two different sizes.\n3. Crop a region containing an important object.\n4. Display the original, resized, and cropped images.\n\n**Goal**: Learn how to control image size and focus areas for computer vision tasks."
    }
  ]
},

{
  "id": 7,
  "html_title": "image-transformations",
  "coursename": "Image Transformations",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are Image Transformations?",
      "bootstrap_title_desc": "Image transformations modify the geometric structure of an image. These operations change the position, orientation, or layout of pixels and are widely used in data augmentation, image alignment, and visual effects."
    },
    {
      "bootstrap_title_heading": "Image Translation",
      "bootstrap_title_desc": "Translation moves an image along the X and Y axes. It shifts the entire image without changing its orientation or size."
    },
    {
      "bootstrap_title_heading": "Example: Translating an Image",
      "bootstrap_title_desc": "Example of shifting an image using OpenCV:\n\n```python\nimport cv2\nimport numpy as np\n\nimg = cv2.imread(\"image.jpg\")\n\n# Translation matrix\nM = np.float32([[1, 0, 50], [0, 1, 30]])\n\ntranslated = cv2.warpAffine(img, M, (img.shape[1], img.shape[0]))\n\ncv2.imshow(\"Translated Image\", translated)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Image Rotation",
      "bootstrap_title_desc": "Rotation turns an image around a fixed point, usually the center. Rotation is useful in data augmentation and correcting tilted images."
    },
    {
      "bootstrap_title_heading": "Example: Rotating an Image",
      "bootstrap_title_desc": "Example of rotating an image by 45 degrees:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\n\n(h, w) = img.shape[:2]\ncenter = (w // 2, h // 2)\n\nM = cv2.getRotationMatrix2D(center, 45, 1.0)\nrotated = cv2.warpAffine(img, M, (w, h))\n\ncv2.imshow(\"Rotated Image\", rotated)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Image Flipping",
      "bootstrap_title_desc": "Flipping creates a mirror image by reversing pixel order. It is commonly used in data augmentation for deep learning."
    },
    {
      "bootstrap_title_heading": "Example: Flipping an Image",
      "bootstrap_title_desc": "Example of flipping an image:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\n\nhorizontal = cv2.flip(img, 1)\nvertical = cv2.flip(img, 0)\n\ncv2.imshow(\"Horizontal Flip\", horizontal)\ncv2.imshow(\"Vertical Flip\", vertical)\n\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Image alignment\n- Data augmentation for CNN models\n- Correcting rotated camera images\n- Visual effects in photo editing"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load an image.\n2. Translate it in different directions.\n3. Rotate it by different angles.\n4. Flip it horizontally and vertically.\n\n**Goal**: Understand how geometric transformations affect images and how they are used in real-world computer vision systems."
    }
  ]
},

{
  "id": 8,
  "html_title": "drawing-on-images",
  "coursename": "Drawing Shapes and Text on Images",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Why Draw on Images?",
      "bootstrap_title_desc": "Drawing shapes and text on images is an important part of computer vision. It is commonly used to visualize detections, highlight regions of interest, label objects, and display results such as bounding boxes and predictions."
    },
    {
      "bootstrap_title_heading": "Drawing Lines",
      "bootstrap_title_desc": "OpenCV provides the `cv2.line()` function to draw straight lines on images. You need to specify the start point, end point, color, and thickness."
    },
    {
      "bootstrap_title_heading": "Example: Draw a Line on an Image",
      "bootstrap_title_desc": "Example of drawing a line:\n\n```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\n\ncv2.line(img, (50, 50), (300, 50), (0, 255, 0), 2)\n\ncv2.imshow(\"Line on Image\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Drawing Rectangles",
      "bootstrap_title_desc": "Rectangles are commonly used to represent bounding boxes around detected objects such as faces or vehicles."
    },
    {
      "bootstrap_title_heading": "Example: Draw a Rectangle",
      "bootstrap_title_desc": "Example of drawing a rectangle:\n\n```python\ncv2.rectangle(img, (100, 100), (300, 300), (255, 0, 0), 2)\n\ncv2.imshow(\"Rectangle on Image\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Drawing Circles",
      "bootstrap_title_desc": "Circles are useful for marking key points such as centers, landmarks, or detected features."
    },
    {
      "bootstrap_title_heading": "Example: Draw a Circle",
      "bootstrap_title_desc": "Example of drawing a circle:\n\n```python\ncv2.circle(img, (200, 200), 50, (0, 0, 255), 2)\n\ncv2.imshow(\"Circle on Image\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Adding Text to Images",
      "bootstrap_title_desc": "Text is added using `cv2.putText()` and is commonly used to display labels, confidence scores, or instructions."
    },
    {
      "bootstrap_title_heading": "Example: Add Text on Image",
      "bootstrap_title_desc": "Example of adding text:\n\n```python\ncv2.putText(img, \"Object Detected\", (50, 450), cv2.FONT_HERSHEY_SIMPLEX, 1, (255, 255, 255), 2)\n\ncv2.imshow(\"Text on Image\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Use Case",
      "bootstrap_title_desc": "Drawing functions are widely used in face detection, object detection, and tracking systems to display bounding boxes and labels on images and videos."
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load an image.\n2. Draw at least one line, rectangle, and circle.\n3. Add text to label the shapes.\n4. Display the final annotated image.\n\n**Goal**: Learn how to visualize computer vision results clearly and effectively."
    }
  ]
},



{
  "id": 9,
  "html_title": "image-filtering-blurring",
  "coursename": "Image Filtering and Blurring",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Image Filtering?",
      "bootstrap_title_desc": "Image filtering is the process of modifying pixel values in an image to enhance important features or remove unwanted noise. Filters are applied using a small matrix called a kernel that slides over the image.\n\nFiltering is a fundamental step in image preprocessing."
    },
    {
      "bootstrap_title_heading": "Why Blurring is Important",
      "bootstrap_title_desc": "Blurring reduces noise and detail in an image. It is commonly used before edge detection, thresholding, and segmentation to improve results."
    },
    {
      "bootstrap_title_heading": "Averaging (Mean) Blur",
      "bootstrap_title_desc": "Averaging blur smooths an image by replacing each pixel with the average of neighboring pixels."
    },
    {
      "bootstrap_title_heading": "Example: Averaging Blur",
      "bootstrap_title_desc": "```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\")\nblur = cv2.blur(img, (5, 5))\n\ncv2.imshow(\"Averaging Blur\", blur)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Gaussian Blur",
      "bootstrap_title_desc": "Gaussian blur uses a Gaussian kernel that gives more weight to the center pixels. It produces smoother and more natural blurring compared to averaging blur."
    },
    {
      "bootstrap_title_heading": "Example: Gaussian Blur",
      "bootstrap_title_desc": "```python\nblur = cv2.GaussianBlur(img, (5, 5), 0)\n\ncv2.imshow(\"Gaussian Blur\", blur)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Median Blur",
      "bootstrap_title_desc": "Median blur replaces each pixel with the median value of neighboring pixels. It is very effective for removing salt-and-pepper noise."
    },
    {
      "bootstrap_title_heading": "Example: Median Blur",
      "bootstrap_title_desc": "```python\nblur = cv2.medianBlur(img, 5)\n\ncv2.imshow(\"Median Blur\", blur)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Bilateral Filter",
      "bootstrap_title_desc": "Bilateral filtering smooths images while preserving edges, making it useful for tasks where edge information is important."
    },
    {
      "bootstrap_title_heading": "Example: Bilateral Filter",
      "bootstrap_title_desc": "```python\nblur = cv2.bilateralFilter(img, 9, 75, 75)\n\ncv2.imshow(\"Bilateral Filter\", blur)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Noise removal in medical images\n- Preprocessing before edge detection\n- Image smoothing in photography\n- Improving segmentation results"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load an image.\n2. Apply averaging, Gaussian, median, and bilateral filters.\n3. Compare the outputs visually.\n4. Identify which filter preserves edges best.\n\n**Goal**: Understand how different filters affect image quality and noise."
    }
  ]
},

{
  "id": 10,
  "html_title": "edge-detection",
  "coursename": "Edge Detection Techniques",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are Edges?",
      "bootstrap_title_desc": "Edges are areas in an image where there is a sharp change in intensity or color. Detecting edges helps identify object boundaries, shapes, and important structural information in images.\n\nEdge detection is a key step in many computer vision tasks such as object detection and image segmentation."
    },
    {
      "bootstrap_title_heading": "Why Edge Detection is Important",
      "bootstrap_title_desc": "- Helps detect object boundaries\n- Reduces amount of data while preserving structure\n- Used in shape detection and feature extraction\n- Improves performance of segmentation algorithms"
    },
    {
      "bootstrap_title_heading": "Sobel Edge Detector",
      "bootstrap_title_desc": "The Sobel operator calculates image gradients in the horizontal and vertical directions. It highlights edges by measuring intensity changes along the X and Y axes."
    },
    {
      "bootstrap_title_heading": "Example: Sobel Edge Detection",
      "bootstrap_title_desc": "```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\", cv2.IMREAD_GRAYSCALE)\n\nsobelx = cv2.Sobel(img, cv2.CV_64F, 1, 0, ksize=3)\nsobely = cv2.Sobel(img, cv2.CV_64F, 0, 1, ksize=3)\n\ncv2.imshow(\"Sobel X\", sobelx)\ncv2.imshow(\"Sobel Y\", sobely)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Laplacian Edge Detector",
      "bootstrap_title_desc": "The Laplacian operator detects edges by calculating the second derivative of the image. It is sensitive to noise and is often used with blurring."
    },
    {
      "bootstrap_title_heading": "Example: Laplacian Edge Detection",
      "bootstrap_title_desc": "```python\nlaplacian = cv2.Laplacian(img, cv2.CV_64F)\n\ncv2.imshow(\"Laplacian\", laplacian)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Canny Edge Detector",
      "bootstrap_title_desc": "Canny is one of the most popular and powerful edge detection algorithms. It uses multiple steps such as noise reduction, gradient calculation, and thresholding to produce clean edges."
    },
    {
      "bootstrap_title_heading": "Example: Canny Edge Detection",
      "bootstrap_title_desc": "```python\nedges = cv2.Canny(img, 100, 200)\n\ncv2.imshow(\"Canny Edges\", edges)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Comparing Edge Detectors",
      "bootstrap_title_desc": "Sobel and Laplacian are simple and fast, but Canny produces cleaner and more accurate edges. Canny is commonly used in real-world applications."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Lane detection in self-driving cars\n- Shape and contour detection\n- Document scanning and OCR\n- Object boundary detection"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load a grayscale image.\n2. Apply Sobel, Laplacian, and Canny edge detection.\n3. Compare the results visually.\n4. Experiment with different threshold values in Canny.\n\n**Goal**: Learn how to extract edges and structural information from images."
    }
  ]
},

{
  "id": 11,
  "html_title": "thresholding-techniques",
  "coursename": "Thresholding Techniques in Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Thresholding?",
      "bootstrap_title_desc": "Thresholding is a technique used to separate objects from the background by converting a grayscale image into a binary image. Pixels are assigned either black or white based on a threshold value.\n\nThresholding simplifies image analysis by reducing complexity."
    },
    {
      "bootstrap_title_heading": "Binary Thresholding",
      "bootstrap_title_desc": "In binary thresholding, pixel values above a threshold are set to white, and values below the threshold are set to black."
    },
    {
      "bootstrap_title_heading": "Example: Binary Thresholding",
      "bootstrap_title_desc": "```python\nimport cv2\n\nimg = cv2.imread(\"image.jpg\", cv2.IMREAD_GRAYSCALE)\n_, thresh = cv2.threshold(img, 127, 255, cv2.THRESH_BINARY)\n\ncv2.imshow(\"Binary Threshold\", thresh)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Inverse Binary Thresholding",
      "bootstrap_title_desc": "Inverse binary thresholding produces the opposite result, where pixel values above the threshold become black and the rest become white."
    },
    {
      "bootstrap_title_heading": "Adaptive Thresholding",
      "bootstrap_title_desc": "Adaptive thresholding calculates different threshold values for different regions of the image. It works well when lighting conditions vary across the image."
    },
    {
      "bootstrap_title_heading": "Example: Adaptive Thresholding",
      "bootstrap_title_desc": "```python\nthresh = cv2.adaptiveThreshold(img, 255, cv2.ADAPTIVE_THRESH_MEAN_C,\n                                cv2.THRESH_BINARY, 11, 2)\n\ncv2.imshow(\"Adaptive Threshold\", thresh)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Otsu’s Thresholding",
      "bootstrap_title_desc": "Otsu’s method automatically calculates the optimal threshold value based on image histogram. It works best for images with clear foreground and background separation."
    },
    {
      "bootstrap_title_heading": "Example: Otsu Thresholding",
      "bootstrap_title_desc": "```python\n_, thresh = cv2.threshold(img, 0, 255,\n                           cv2.THRESH_BINARY + cv2.THRESH_OTSU)\n\ncv2.imshow(\"Otsu Threshold\", thresh)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "When to Use Thresholding",
      "bootstrap_title_desc": "- Document scanning\n- License plate detection\n- Object segmentation\n- Preprocessing for contour detection"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load a grayscale image.\n2. Apply binary, adaptive, and Otsu thresholding.\n3. Compare the results visually.\n4. Try different threshold values and observe changes.\n\n**Goal**: Learn how to segment images using different thresholding techniques."
    }
  ]
},

{
  "id": 12,
  "html_title": "morphological-operations",
  "coursename": "Morphological Operations in Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are Morphological Operations?",
      "bootstrap_title_desc": "Morphological operations are image processing techniques that operate on the shape and structure of objects in a binary or grayscale image. They are mainly used to remove noise, fill gaps, and enhance object boundaries.\n\nThese operations use a structuring element (kernel) to probe the image."
    },
    {
      "bootstrap_title_heading": "Structuring Element (Kernel)",
      "bootstrap_title_desc": "A structuring element is a small matrix used in morphological operations. Its shape and size affect the result of operations such as erosion and dilation.\n\nCommon shapes include square, rectangle, and circle."
    },
    {
      "bootstrap_title_heading": "Erosion",
      "bootstrap_title_desc": "Erosion removes pixels from object boundaries. It is useful for eliminating small noise and separating objects that are connected."
    },
    {
      "bootstrap_title_heading": "Example: Erosion",
      "bootstrap_title_desc": "```python\nimport cv2\nimport numpy as np\n\nimg = cv2.imread(\"binary.png\", cv2.IMREAD_GRAYSCALE)\nkernel = np.ones((5,5), np.uint8)\n\neroded = cv2.erode(img, kernel, iterations=1)\n\ncv2.imshow(\"Eroded Image\", eroded)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Dilation",
      "bootstrap_title_desc": "Dilation adds pixels to object boundaries. It is useful for filling small holes and connecting broken parts of objects."
    },
    {
      "bootstrap_title_heading": "Example: Dilation",
      "bootstrap_title_desc": "```python\ndilated = cv2.dilate(img, kernel, iterations=1)\n\ncv2.imshow(\"Dilated Image\", dilated)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Opening",
      "bootstrap_title_desc": "Opening is erosion followed by dilation. It is mainly used to remove small noise while preserving the shape of larger objects."
    },
    {
      "bootstrap_title_heading": "Example: Opening",
      "bootstrap_title_desc": "```python\nopening = cv2.morphologyEx(img, cv2.MORPH_OPEN, kernel)\n\ncv2.imshow(\"Opening\", opening)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Closing",
      "bootstrap_title_desc": "Closing is dilation followed by erosion. It is used to fill small holes and gaps inside objects."
    },
    {
      "bootstrap_title_heading": "Example: Closing",
      "bootstrap_title_desc": "```python\nclosing = cv2.morphologyEx(img, cv2.MORPH_CLOSE, kernel)\n\ncv2.imshow(\"Closing\", closing)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Noise removal in scanned documents\n- Improving thresholded images\n- Preprocessing for contour detection\n- Shape enhancement in medical images"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Apply erosion and dilation on a binary image.\n2. Use opening to remove noise.\n3. Use closing to fill gaps.\n4. Compare all results visually.\n\n**Goal**: Understand how morphological operations improve image structure."
    }
  ]
},


{
  "id": 13,
  "html_title": "contour-detection",
  "coursename": "Contour Detection and Analysis",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are Contours?",
      "bootstrap_title_desc": "Contours are curves that join continuous points along the boundary of objects with the same color or intensity. In computer vision, contours are widely used for shape analysis, object detection, and image segmentation.\n\nContours work best on binary images."
    },
    {
      "bootstrap_title_heading": "Why Contours are Important",
      "bootstrap_title_desc": "- Detect object boundaries\n- Analyze shapes and sizes\n- Count objects in an image\n- Used in object detection and tracking"
    },
    {
      "bootstrap_title_heading": "Finding Contours in OpenCV",
      "bootstrap_title_desc": "OpenCV provides the `cv2.findContours()` function to detect contours from a binary image."
    },
    {
      "bootstrap_title_heading": "Example: Finding and Drawing Contours",
      "bootstrap_title_desc": "```python\nimport cv2\n\nimg = cv2.imread(\"binary.png\")\ngray = cv2.cvtColor(img, cv2.COLOR_BGR2GRAY)\n_, thresh = cv2.threshold(gray, 127, 255, cv2.THRESH_BINARY)\n\ncontours, _ = cv2.findContours(thresh, cv2.RETR_EXTERNAL, cv2.CHAIN_APPROX_SIMPLE)\n\ncv2.drawContours(img, contours, -1, (0, 255, 0), 2)\n\ncv2.imshow(\"Contours\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Contour Retrieval Modes",
      "bootstrap_title_desc": "- `RETR_EXTERNAL`: Retrieves only outer contours\n- `RETR_TREE`: Retrieves all contours and hierarchy\n- `RETR_LIST`: Retrieves all contours without hierarchy"
    },
    {
      "bootstrap_title_heading": "Contour Approximation Methods",
      "bootstrap_title_desc": "- `CHAIN_APPROX_SIMPLE`: Compresses contour points\n- `CHAIN_APPROX_NONE`: Stores all contour points"
    },
    {
      "bootstrap_title_heading": "Contour Properties",
      "bootstrap_title_desc": "Once contours are detected, various properties can be calculated:\n\n- Contour area\n- Perimeter\n- Bounding rectangle\n- Centroid"
    },
    {
      "bootstrap_title_heading": "Example: Contour Area and Bounding Box",
      "bootstrap_title_desc": "```python\nfor cnt in contours:\n    area = cv2.contourArea(cnt)\n    x, y, w, h = cv2.boundingRect(cnt)\n    cv2.rectangle(img, (x, y), (x+w, y+h), (255, 0, 0), 2)\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Shape detection\n- Object counting\n- Document analysis\n- Traffic sign detection"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load a binary image.\n2. Detect contours.\n3. Draw bounding boxes around objects.\n4. Print contour areas.\n\n**Goal**: Learn how to detect and analyze object shapes using contours."
    }
  ]
},

{
  "id": 14,
  "html_title": "feature-detection",
  "coursename": "Feature Detection: Corners and Keypoints",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are Image Features?",
      "bootstrap_title_desc": "Image features are distinctive and informative parts of an image such as corners, edges, and keypoints. These features are stable under changes in scale, rotation, and lighting, making them useful for tasks like object recognition and image matching."
    },
    {
      "bootstrap_title_heading": "What is Corner Detection?",
      "bootstrap_title_desc": "Corners are points in an image where the intensity changes significantly in multiple directions. Corners are more informative than edges because they contain unique local information."
    },
    {
      "bootstrap_title_heading": "Harris Corner Detector",
      "bootstrap_title_desc": "The Harris Corner Detector is one of the earliest and most popular methods for detecting corners in an image."
    },
    {
      "bootstrap_title_heading": "Example: Harris Corner Detection",
      "bootstrap_title_desc": "```python\nimport cv2\nimport numpy as np\n\nimg = cv2.imread(\"image.jpg\")\ngray = cv2.cvtColor(img, cv2.COLOR_BGR2GRAY)\ngray = np.float32(gray)\n\ncorners = cv2.cornerHarris(gray, 2, 3, 0.04)\nimg[corners > 0.01 * corners.max()] = [0, 0, 255]\n\ncv2.imshow(\"Harris Corners\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Shi-Tomasi Corner Detector",
      "bootstrap_title_desc": "Shi-Tomasi is an improved version of the Harris detector. It selects the best corners based on quality score."
    },
    {
      "bootstrap_title_heading": "Example: Shi-Tomasi Corner Detection",
      "bootstrap_title_desc": "```python\ncorners = cv2.goodFeaturesToTrack(gray, 100, 0.01, 10)\nfor corner in corners:\n    x, y = corner.ravel()\n    cv2.circle(img, (int(x), int(y)), 3, (0, 255, 0), -1)\n```"
    },
    {
      "bootstrap_title_heading": "Keypoint Detection",
      "bootstrap_title_desc": "Keypoints are special feature points that can be detected and described. They are used for matching images and recognizing objects."
    },
    {
      "bootstrap_title_heading": "ORB Feature Detector",
      "bootstrap_title_desc": "ORB (Oriented FAST and Rotated BRIEF) is a fast and efficient feature detector and descriptor that is free to use and suitable for real-time applications."
    },
    {
      "bootstrap_title_heading": "Example: ORB Feature Detection",
      "bootstrap_title_desc": "```python\norb = cv2.ORB_create()\nkeypoints, descriptors = orb.detectAndCompute(gray, None)\n\nimg_kp = cv2.drawKeypoints(img, keypoints, None, color=(0, 255, 0))\ncv2.imshow(\"ORB Keypoints\", img_kp)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Image matching\n- Object recognition\n- Panorama stitching\n- Visual SLAM"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Detect corners using Harris and Shi-Tomasi.\n2. Detect ORB keypoints.\n3. Visualize all detected features.\n4. Compare results.\n\n**Goal**: Understand how feature detection helps computers identify important points in images."
    }
  ]
},

{
  "id": 15,
  "html_title": "face-detection-opencv",
  "coursename": "Face Detection using OpenCV",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Face Detection?",
      "bootstrap_title_desc": "Face detection is a computer vision technique used to identify and locate human faces in images or videos. It determines the position and size of faces but does not recognize who the person is.\n\nFace detection is often the first step in face recognition systems."
    },
    {
      "bootstrap_title_heading": "Haar Cascade Classifier",
      "bootstrap_title_desc": "Haar Cascade is a machine learning-based object detection method provided by OpenCV. It uses Haar-like features and a cascade of classifiers to quickly detect faces.\n\nIt works best for frontal faces and good lighting conditions."
    },
    {
      "bootstrap_title_heading": "Downloading Haar Cascade Model",
      "bootstrap_title_desc": "OpenCV provides pre-trained Haar Cascade XML files. Commonly used file:\n\n- `haarcascade_frontalface_default.xml`\n\nPlace this file in your project directory before using it."
    },
    {
      "bootstrap_title_heading": "Face Detection on an Image",
      "bootstrap_title_desc": "Example of detecting faces in an image:\n\n```python\nimport cv2\n\nface_cascade = cv2.CascadeClassifier(\"haarcascade_frontalface_default.xml\")\n\nimg = cv2.imread(\"group.jpg\")\ngray = cv2.cvtColor(img, cv2.COLOR_BGR2GRAY)\n\nfaces = face_cascade.detectMultiScale(gray, scaleFactor=1.1, minNeighbors=5)\n\nfor (x, y, w, h) in faces:\n    cv2.rectangle(img, (x, y), (x+w, y+h), (0, 255, 0), 2)\n\ncv2.imshow(\"Face Detection\", img)\ncv2.waitKey(0)\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Understanding detectMultiScale Parameters",
      "bootstrap_title_desc": "- **scaleFactor**: Controls image scaling (smaller values increase accuracy but slow detection)\n- **minNeighbors**: Controls detection quality (higher values reduce false positives)\n- **minSize**: Minimum object size"
    },
    {
      "bootstrap_title_heading": "Real-Time Face Detection using Webcam",
      "bootstrap_title_desc": "Example of detecting faces from a webcam feed:\n\n```python\ncap = cv2.VideoCapture(0)\n\nwhile True:\n    ret, frame = cap.read()\n    gray = cv2.cvtColor(frame, cv2.COLOR_BGR2GRAY)\n    faces = face_cascade.detectMultiScale(gray, 1.1, 5)\n\n    for (x, y, w, h) in faces:\n        cv2.rectangle(frame, (x, y), (x+w, y+h), (255, 0, 0), 2)\n\n    cv2.imshow(\"Webcam Face Detection\", frame)\n    if cv2.waitKey(1) & 0xFF == 27:\n        break\n\ncap.release()\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Limitations of Haar Cascade",
      "bootstrap_title_desc": "- Sensitive to lighting conditions\n- Works best for frontal faces\n- Not robust for complex backgrounds\n\nModern systems use deep learning-based detectors for higher accuracy."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Face unlock systems\n- Attendance systems\n- Security and surveillance\n- Camera filters"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Detect faces in a group photo.\n2. Experiment with different `scaleFactor` and `minNeighbors` values.\n3. Try real-time face detection using a webcam.\n\n**Goal**: Build a complete face detection system using OpenCV."
    }
  ]
},





{
  "id": 16,
  "html_title": "video-processing-basics",
  "coursename": "Video Processing Basics with OpenCV",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Video Processing?",
      "bootstrap_title_desc": "Video processing involves analyzing and manipulating video data frame by frame. A video is essentially a sequence of images (frames) shown rapidly. Computer vision algorithms are applied to each frame to detect, track, or recognize objects over time."
    },
    {
      "bootstrap_title_heading": "Understanding Video Frames",
      "bootstrap_title_desc": "Each video consists of multiple frames per second (FPS). Processing video means reading each frame, applying operations, and displaying the result in real time."
    },
    {
      "bootstrap_title_heading": "Reading Video from a File",
      "bootstrap_title_desc": "OpenCV provides the `cv2.VideoCapture()` function to read video files frame by frame."
    },
    {
      "bootstrap_title_heading": "Example: Read and Display Video File",
      "bootstrap_title_desc": "```python\nimport cv2\n\ncap = cv2.VideoCapture(\"video.mp4\")\n\nwhile cap.isOpened():\n    ret, frame = cap.read()\n    if not ret:\n        break\n\n    cv2.imshow(\"Video\", frame)\n\n    if cv2.waitKey(25) & 0xFF == 27:\n        break\n\ncap.release()\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Capturing Video from Webcam",
      "bootstrap_title_desc": "Webcams can be accessed by passing `0` (or camera index) to `VideoCapture`."
    },
    {
      "bootstrap_title_heading": "Example: Webcam Video Capture",
      "bootstrap_title_desc": "```python\ncap = cv2.VideoCapture(0)\n\nwhile True:\n    ret, frame = cap.read()\n    cv2.imshow(\"Webcam Feed\", frame)\n\n    if cv2.waitKey(1) & 0xFF == 27:\n        break\n\ncap.release()\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Converting Video Frames to Grayscale",
      "bootstrap_title_desc": "Video frames can be converted to grayscale to simplify processing and improve performance."
    },
    {
      "bootstrap_title_heading": "Example: Grayscale Video Processing",
      "bootstrap_title_desc": "```python\ngray = cv2.cvtColor(frame, cv2.COLOR_BGR2GRAY)\ncv2.imshow(\"Grayscale Video\", gray)\n```"
    },
    {
      "bootstrap_title_heading": "Understanding Frame Rate (FPS)",
      "bootstrap_title_desc": "Frame rate controls how fast frames are displayed. `cv2.waitKey()` plays an important role in controlling video speed."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- CCTV surveillance\n- Live face detection\n- Motion analysis\n- Sports analytics"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Read a video file and display it.\n2. Capture live video from a webcam.\n3. Convert video frames to grayscale.\n4. Exit the video loop using a keyboard key.\n\n**Goal**: Understand how videos are processed frame by frame in computer vision."
    }
  ]
},


{
  "id": 17,
  "html_title": "motion-detection",
  "coursename": "Motion Detection in Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Motion Detection?",
      "bootstrap_title_desc": "Motion detection is the process of identifying changes in position of objects over time in a video stream. It works by comparing consecutive video frames to detect areas where movement occurs."
    },
    {
      "bootstrap_title_heading": "Why Motion Detection is Important",
      "bootstrap_title_desc": "- Security and surveillance systems\n- Intrusion detection\n- Human activity recognition\n- Traffic monitoring"
    },
    {
      "bootstrap_title_heading": "Frame Differencing",
      "bootstrap_title_desc": "Frame differencing detects motion by calculating the difference between consecutive frames. Areas with significant changes are considered moving regions."
    },
    {
      "bootstrap_title_heading": "Example: Motion Detection using Frame Differencing",
      "bootstrap_title_desc": "```python\nimport cv2\n\ncap = cv2.VideoCapture(0)\nret, frame1 = cap.read()\nret, frame2 = cap.read()\n\nwhile cap.isOpened():\n    diff = cv2.absdiff(frame1, frame2)\n    gray = cv2.cvtColor(diff, cv2.COLOR_BGR2GRAY)\n    blur = cv2.GaussianBlur(gray, (5, 5), 0)\n    _, thresh = cv2.threshold(blur, 20, 255, cv2.THRESH_BINARY)\n\n    cv2.imshow(\"Motion\", thresh)\n\n    frame1 = frame2\n    ret, frame2 = cap.read()\n    if not ret or cv2.waitKey(30) & 0xFF == 27:\n        break\n\ncap.release()\ncv2.destroyAllWindows()\n```"
    },
    {
      "bootstrap_title_heading": "Background Subtraction",
      "bootstrap_title_desc": "Background subtraction separates moving objects from the static background. OpenCV provides built-in background subtractor algorithms."
    },
    {
      "bootstrap_title_heading": "Example: Background Subtraction using MOG2",
      "bootstrap_title_desc": "```python\nfgbg = cv2.createBackgroundSubtractorMOG2()\n\nwhile True:\n    ret, frame = cap.read()\n    fgmask = fgbg.apply(frame)\n\n    cv2.imshow(\"Foreground Mask\", fgmask)\n\n    if cv2.waitKey(30) & 0xFF == 27:\n        break\n```"
    },
    {
      "bootstrap_title_heading": "Noise Removal in Motion Detection",
      "bootstrap_title_desc": "Morphological operations like erosion and dilation are used to remove noise from motion masks and improve detection accuracy."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- CCTV surveillance systems\n- Smart home security\n- Automatic door systems\n- Traffic flow analysis"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Detect motion using frame differencing.\n2. Try background subtraction using MOG2.\n3. Apply thresholding and blurring.\n4. Observe moving regions in real time.\n\n**Goal**: Build a basic motion detection system using OpenCV."
    }
  ]
}
,

{
  "id": 18,
  "html_title": "deep-learning-introduction",
  "coursename": "Introduction to Deep Learning for Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Deep Learning?",
      "bootstrap_title_desc": "Deep Learning is a subset of machine learning that uses neural networks with multiple layers to learn patterns from data. In computer vision, deep learning models automatically learn features from images instead of relying on hand-crafted rules."
    },
    {
      "bootstrap_title_heading": "Why Deep Learning for Computer Vision?",
      "bootstrap_title_desc": "- Automatically learns features from images\n- Handles complex visual patterns\n- Achieves higher accuracy than traditional methods\n- Used in modern applications like face recognition and self-driving cars"
    },
    {
      "bootstrap_title_heading": "Traditional Computer Vision vs Deep Learning",
      "bootstrap_title_desc": "Traditional computer vision relies on manually designed features such as edges and corners. Deep learning replaces this with neural networks that learn features directly from raw pixel data."
    },
    {
      "bootstrap_title_heading": "What is a Neural Network?",
      "bootstrap_title_desc": "A neural network is a computational model inspired by the human brain. It consists of layers of neurons that process input data and produce output predictions."
    },
    {
      "bootstrap_title_heading": "Basic Structure of a Neural Network",
      "bootstrap_title_desc": "- Input Layer: Receives image data\n- Hidden Layers: Learn features\n- Output Layer: Produces prediction or class label"
    },
    {
      "bootstrap_title_heading": "Why CNNs for Images?",
      "bootstrap_title_desc": "Convolutional Neural Networks (CNNs) are specially designed for image data. They use convolution operations to capture spatial patterns like edges, textures, and shapes."
    },
    {
      "bootstrap_title_heading": "Simple Example: Image Classification Concept",
      "bootstrap_title_desc": "In image classification, a model receives an image and predicts its class (e.g., cat or dog). The model learns by comparing its predictions with true labels and adjusting its internal weights."
    },
    {
      "bootstrap_title_heading": "Deep Learning Frameworks",
      "bootstrap_title_desc": "Popular frameworks used for deep learning in computer vision include:\n\n- TensorFlow\n- Keras\n- PyTorch\n\nThese frameworks simplify building and training neural networks."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Face recognition\n- Image classification\n- Object detection\n- Medical image analysis\n- Autonomous vehicles"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Understand the difference between traditional CV and deep learning.\n2. Identify where deep learning is used in everyday applications.\n3. Install TensorFlow or PyTorch (optional).\n\n**Goal**: Build a strong conceptual foundation for deep learning in computer vision."
    }
  ]
},


{
  "id": 19,
  "html_title": "cnn-fundamentals",
  "coursename": "CNN Fundamentals: Convolution, Filters, and Feature Maps",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is a Convolutional Neural Network (CNN)?",
      "bootstrap_title_desc": "A Convolutional Neural Network (CNN) is a deep learning model specifically designed for image data. CNNs automatically learn visual features such as edges, textures, and shapes directly from images using convolution operations."
    },
    {
      "bootstrap_title_heading": "Why CNNs are Better than Traditional Neural Networks for Images",
      "bootstrap_title_desc": "- Preserves spatial information\n- Fewer parameters than fully connected networks\n- Automatically learns hierarchical features\n- Works well for large image datasets"
    },
    {
      "bootstrap_title_heading": "What is Convolution?",
      "bootstrap_title_desc": "Convolution is a mathematical operation where a small matrix called a filter (or kernel) slides over the image and computes dot products to extract features such as edges and patterns."
    },
    {
      "bootstrap_title_heading": "Filters (Kernels)",
      "bootstrap_title_desc": "Filters are small matrices (e.g., 3×3, 5×5) that detect specific patterns in images.\n\nDifferent filters learn different features like vertical edges, horizontal edges, and textures."
    },
    {
      "bootstrap_title_heading": "Stride and Padding",
      "bootstrap_title_desc": "- **Stride**: Number of pixels the filter moves at each step\n- **Padding**: Adds extra pixels around the image to control output size\n\nThese parameters affect feature map size and information retention."
    },
    {
      "bootstrap_title_heading": "Feature Maps",
      "bootstrap_title_desc": "The output of a convolution operation is called a feature map. Each feature map highlights where a specific feature appears in the image."
    },
    {
      "bootstrap_title_heading": "Visual Example of Convolution Concept",
      "bootstrap_title_desc": "Think of convolution as scanning an image with a magnifying glass that looks for specific patterns like edges or corners."
    },
    {
      "bootstrap_title_heading": "Simple CNN Conceptual Example",
      "bootstrap_title_desc": "Input Image → Convolution → Feature Maps → Pooling → Fully Connected Layer → Prediction"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Image classification (cats vs dogs)\n- Face recognition\n- Object detection\n- Medical image diagnosis"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Understand how convolution works on images.\n2. Learn the effect of filter size, stride, and padding.\n3. Visualize how feature maps are generated.\n\n**Goal**: Build a strong foundation of CNN internals before coding models."
    }
  ]
},

{
  "id": 20,
  "html_title": "cnn-layers",
  "coursename": "CNN Layers: Pooling, Activation, and Fully Connected Layers",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Overview of CNN Layers",
      "bootstrap_title_desc": "A Convolutional Neural Network is built using multiple types of layers. Each layer has a specific role in extracting features and making predictions. Understanding these layers is essential before building CNN models."
    },
    {
      "bootstrap_title_heading": "Pooling Layer",
      "bootstrap_title_desc": "Pooling layers reduce the spatial size of feature maps while preserving important information. This helps reduce computation and prevents overfitting."
    },
    {
      "bootstrap_title_heading": "Types of Pooling",
      "bootstrap_title_desc": "- **Max Pooling**: Takes the maximum value from a region\n- **Average Pooling**: Takes the average value from a region\n\nMax pooling is most commonly used in CNNs."
    },
    {
      "bootstrap_title_heading": "Example: Max Pooling Concept",
      "bootstrap_title_desc": "Max pooling selects the strongest feature from a region, helping the network focus on the most important patterns."
    },
    {
      "bootstrap_title_heading": "Activation Functions",
      "bootstrap_title_desc": "Activation functions introduce non-linearity into the network, allowing CNNs to learn complex patterns."
    },
    {
      "bootstrap_title_heading": "Common Activation Functions",
      "bootstrap_title_desc": "- **ReLU (Rectified Linear Unit)**: Most commonly used\n- **Sigmoid**: Used for binary outputs\n- **Softmax**: Used for multi-class classification"
    },
    {
      "bootstrap_title_heading": "Fully Connected Layer",
      "bootstrap_title_desc": "Fully connected layers connect all neurons from the previous layer. They combine extracted features to make final predictions."
    },
    {
      "bootstrap_title_heading": "Flattening in CNNs",
      "bootstrap_title_desc": "Before entering the fully connected layer, feature maps are flattened into a 1D vector."
    },
    {
      "bootstrap_title_heading": "CNN Layer Flow",
      "bootstrap_title_desc": "Convolution → Activation → Pooling → Flatten → Fully Connected → Output"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Image classification\n- Face recognition\n- Medical image analysis\n- Traffic sign recognition"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Identify different layers in a CNN architecture.\n2. Understand the role of pooling and activation functions.\n3. Draw a simple CNN layer diagram.\n\n**Goal**: Understand how CNN layers work together to classify images."
    }
  ]
},

{
  "id": 21,
  "html_title": "first-cnn-model",
  "coursename": "Building Your First CNN Model",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What Will We Build Today?",
      "bootstrap_title_desc": "In this lesson, we will build a simple Convolutional Neural Network (CNN) for image classification. This model will help you understand how all CNN layers work together to make predictions."
    },
    {
      "bootstrap_title_heading": "Problem Statement: Image Classification",
      "bootstrap_title_desc": "Image classification is the task of assigning a label to an image based on its content. Examples include classifying handwritten digits, animals, or everyday objects."
    },
    {
      "bootstrap_title_heading": "Dataset Concept (MNIST / Sample Images)",
      "bootstrap_title_desc": "For beginners, datasets like MNIST (handwritten digits) are commonly used. Images are small, grayscale, and labeled, making them ideal for learning CNN basics."
    },
    {
      "bootstrap_title_heading": "Basic Steps to Build a CNN",
      "bootstrap_title_desc": "1. Load dataset\n2. Preprocess images\n3. Build CNN architecture\n4. Compile the model\n5. Train the model\n6. Evaluate performance"
    },
    {
      "bootstrap_title_heading": "Importing Required Libraries",
      "bootstrap_title_desc": "```python\nimport tensorflow as tf\nfrom tensorflow.keras.models import Sequential\nfrom tensorflow.keras.layers import Conv2D, MaxPooling2D, Flatten, Dense\n```"
    },
    {
      "bootstrap_title_heading": "Building a Simple CNN Architecture",
      "bootstrap_title_desc": "Below is a basic CNN model structure:\n\n```python\nmodel = Sequential([\n    Conv2D(32, (3,3), activation='relu', input_shape=(28,28,1)),\n    MaxPooling2D((2,2)),\n    Conv2D(64, (3,3), activation='relu'),\n    MaxPooling2D((2,2)),\n    Flatten(),\n    Dense(128, activation='relu'),\n    Dense(10, activation='softmax')\n])\n```"
    },
    {
      "bootstrap_title_heading": "Compiling the CNN Model",
      "bootstrap_title_desc": "The model must be compiled by specifying loss function, optimizer, and evaluation metrics:\n\n```python\nmodel.compile(\n    optimizer='adam',\n    loss='sparse_categorical_crossentropy',\n    metrics=['accuracy']\n)\n```"
    },
    {
      "bootstrap_title_heading": "Training the Model",
      "bootstrap_title_desc": "Training means teaching the model to learn from data:\n\n```python\nmodel.fit(x_train, y_train, epochs=5, validation_split=0.1)\n```"
    },
    {
      "bootstrap_title_heading": "Evaluating Model Performance",
      "bootstrap_title_desc": "After training, the model is evaluated on test data:\n\n```python\nmodel.evaluate(x_test, y_test)\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Digit recognition\n- Basic object classification\n- Foundation for advanced CNN models\n- Educational and research projects"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Build a CNN model using the given architecture.\n2. Train it on a sample image dataset.\n3. Observe training accuracy and loss.\n4. Modify number of layers or filters and compare results.\n\n**Goal**: Learn how to build, train, and evaluate a basic CNN model."
    }
  ]
},

{
  "id": 22,
  "html_title": "image-classification-pipeline",
  "coursename": "Image Classification Pipeline",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is an Image Classification Pipeline?",
      "bootstrap_title_desc": "An image classification pipeline is a structured process that transforms raw images into predicted class labels using machine learning or deep learning models. Each step in the pipeline plays a crucial role in achieving accurate predictions."
    },
    {
      "bootstrap_title_heading": "Step 1: Dataset Collection",
      "bootstrap_title_desc": "The first step is collecting labeled images. Each image should belong to a specific class (for example: cat, dog, car). Data quality and diversity directly affect model performance."
    },
    {
      "bootstrap_title_heading": "Step 2: Dataset Splitting",
      "bootstrap_title_desc": "The dataset is divided into:\n\n- Training set (to train the model)\n- Validation set (to tune parameters)\n- Test set (to evaluate performance)\n\nTypical split: 70% train, 15% validation, 15% test."
    },
    {
      "bootstrap_title_heading": "Step 3: Image Preprocessing",
      "bootstrap_title_desc": "Preprocessing prepares images for the model:\n\n- Resize images to fixed size\n- Normalize pixel values\n- Convert color formats if needed"
    },
    {
      "bootstrap_title_heading": "Example: Image Normalization",
      "bootstrap_title_desc": "```python\nx_train = x_train / 255.0\nx_test = x_test / 255.0\n```"
    },
    {
      "bootstrap_title_heading": "Step 4: Model Selection",
      "bootstrap_title_desc": "Choose a suitable CNN architecture based on the problem size and complexity. For small datasets, a simple CNN works well. For larger datasets, pretrained models are preferred."
    },
    {
      "bootstrap_title_heading": "Step 5: Model Training",
      "bootstrap_title_desc": "Train the model using the training dataset and monitor performance using validation data."
    },
    {
      "bootstrap_title_heading": "Step 6: Model Evaluation",
      "bootstrap_title_desc": "Evaluate model performance using metrics such as accuracy, precision, recall, and confusion matrix."
    },
    {
      "bootstrap_title_heading": "Step 7: Model Inference",
      "bootstrap_title_desc": "Inference means using the trained model to predict classes for new, unseen images."
    },
    {
      "bootstrap_title_heading": "End-to-End Pipeline Summary",
      "bootstrap_title_desc": "Collect Data → Preprocess → Train Model → Evaluate → Predict"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Medical image classification\n- Wildlife monitoring\n- Product categorization\n- Face recognition systems"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Prepare a small image dataset with two classes.\n2. Split the dataset into train and test sets.\n3. Normalize the images.\n4. Train a CNN and evaluate accuracy.\n\n**Goal**: Understand the complete workflow of image classification."
    }
  ]
},



{
  "id": 23,
  "html_title": "data-augmentation",
  "coursename": "Data Augmentation for Image Classification",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Data Augmentation?",
      "bootstrap_title_desc": "Data augmentation is a technique used to artificially increase the size and diversity of a training dataset by applying random transformations to existing images. It helps deep learning models generalize better and reduces overfitting."
    },
    {
      "bootstrap_title_heading": "Why Data Augmentation is Important",
      "bootstrap_title_desc": "- Improves model generalization\n- Reduces overfitting\n- Helps when dataset size is small\n- Makes model robust to real-world variations"
    },
    {
      "bootstrap_title_heading": "Common Data Augmentation Techniques",
      "bootstrap_title_desc": "- Image rotation\n- Horizontal and vertical flipping\n- Zooming\n- Width and height shifting\n- Brightness adjustment"
    },
    {
      "bootstrap_title_heading": "Data Augmentation in CNN Training",
      "bootstrap_title_desc": "Data augmentation is applied only to the training dataset, not validation or test data. This ensures fair evaluation of the model."
    },
    {
      "bootstrap_title_heading": "Example: Data Augmentation using Keras",
      "bootstrap_title_desc": "```python\nfrom tensorflow.keras.preprocessing.image import ImageDataGenerator\n\ndatagen = ImageDataGenerator(\n    rotation_range=20,\n    width_shift_range=0.2,\n    height_shift_range=0.2,\n    zoom_range=0.2,\n    horizontal_flip=True\n)\n\ndatagen.fit(x_train)\n```"
    },
    {
      "bootstrap_title_heading": "Visualizing Augmented Images",
      "bootstrap_title_desc": "Augmented images help visualize how the model sees different variations of the same data. This improves robustness to real-world scenarios."
    },
    {
      "bootstrap_title_heading": "Overfitting vs Data Augmentation",
      "bootstrap_title_desc": "Overfitting occurs when a model performs well on training data but poorly on new data. Data augmentation helps prevent this by exposing the model to varied image patterns."
    },
    {
      "bootstrap_title_heading": "When to Use Data Augmentation",
      "bootstrap_title_desc": "- Small datasets\n- Complex image classification tasks\n- When model overfits quickly"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Medical imaging\n- Facial recognition\n- Autonomous driving\n- Satellite image analysis"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Apply data augmentation to a training dataset.\n2. Visualize augmented images.\n3. Train a CNN with and without augmentation.\n4. Compare model accuracy.\n\n**Goal**: Learn how data augmentation improves CNN performance."
    }
  ]
},


{
  "id": 24,
  "html_title": "model-evaluation-metrics",
  "coursename": "Model Evaluation Metrics for Image Classification",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Why Model Evaluation is Important",
      "bootstrap_title_desc": "Model evaluation helps measure how well a trained model performs on unseen data. Accuracy alone is often not sufficient, especially when dealing with imbalanced datasets."
    },
    {
      "bootstrap_title_heading": "Confusion Matrix",
      "bootstrap_title_desc": "A confusion matrix summarizes prediction results by comparing actual labels with predicted labels. It helps understand types of errors made by the model.\n\n- True Positive (TP)\n- True Negative (TN)\n- False Positive (FP)\n- False Negative (FN)"
    },
    {
      "bootstrap_title_heading": "Accuracy",
      "bootstrap_title_desc": "Accuracy measures the percentage of correct predictions:\n\nAccuracy = (TP + TN) / Total Predictions\n\nIt works well when classes are balanced."
    },
    {
      "bootstrap_title_heading": "Precision",
      "bootstrap_title_desc": "Precision measures how many predicted positives are actually correct:\n\nPrecision = TP / (TP + FP)\n\nIt is important when false positives are costly."
    },
    {
      "bootstrap_title_heading": "Recall",
      "bootstrap_title_desc": "Recall measures how many actual positives were correctly predicted:\n\nRecall = TP / (TP + FN)\n\nIt is important when missing a positive case is costly."
    },
    {
      "bootstrap_title_heading": "F1-Score",
      "bootstrap_title_desc": "F1-score is the harmonic mean of precision and recall. It balances both metrics and is useful when classes are imbalanced."
    },
    {
      "bootstrap_title_heading": "Example: Classification Report",
      "bootstrap_title_desc": "```python\nfrom sklearn.metrics import classification_report\n\nprint(classification_report(y_test, y_pred))\n```"
    },
    {
      "bootstrap_title_heading": "ROC Curve and AUC",
      "bootstrap_title_desc": "ROC curve shows the trade-off between true positive rate and false positive rate. AUC measures the overall performance of the classifier."
    },
    {
      "bootstrap_title_heading": "When to Use Which Metric",
      "bootstrap_title_desc": "- Accuracy: Balanced datasets\n- Precision: Spam detection\n- Recall: Medical diagnosis\n- F1-score: Imbalanced datasets"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Medical image classification\n- Fraud detection\n- Face recognition\n- Quality inspection systems"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Generate predictions using a trained CNN.\n2. Compute accuracy, precision, recall, and F1-score.\n3. Analyze confusion matrix.\n\n**Goal**: Learn how to evaluate CNN models effectively."
    }
  ]
},

{
  "id": 25,
  "html_title": "transfer-learning",
  "coursename": "Transfer Learning for Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Transfer Learning?",
      "bootstrap_title_desc": "Transfer learning is a technique where a model trained on a large dataset is reused for a new but related task. Instead of training a CNN from scratch, we leverage knowledge learned from existing models.\n\nThis approach saves time, computational resources, and improves performance on small datasets."
    },
    {
      "bootstrap_title_heading": "Why Use Transfer Learning?",
      "bootstrap_title_desc": "- Works well with limited data\n- Faster training\n- Higher accuracy\n- Uses proven architectures"
    },
    {
      "bootstrap_title_heading": "Pretrained Models",
      "bootstrap_title_desc": "Pretrained models are CNNs trained on large datasets like ImageNet. Common pretrained models include:\n\n- VGG16 / VGG19\n- ResNet\n- Inception\n- MobileNet\n- EfficientNet"
    },
    {
      "bootstrap_title_heading": "Feature Extraction vs Fine-Tuning",
      "bootstrap_title_desc": "- **Feature Extraction**: Freeze convolution layers and train only classifier\n- **Fine-Tuning**: Unfreeze some layers and retrain them with a low learning rate"
    },
    {
      "bootstrap_title_heading": "Example: Loading a Pretrained Model",
      "bootstrap_title_desc": "```python\nfrom tensorflow.keras.applications import VGG16\n\nbase_model = VGG16(weights='imagenet', include_top=False, input_shape=(224,224,3))\nbase_model.trainable = False\n```"
    },
    {
      "bootstrap_title_heading": "Adding Custom Classification Layers",
      "bootstrap_title_desc": "```python\nfrom tensorflow.keras.models import Sequential\nfrom tensorflow.keras.layers import Flatten, Dense\n\nmodel = Sequential([\n    base_model,\n    Flatten(),\n    Dense(128, activation='relu'),\n    Dense(2, activation='softmax')\n])\n```"
    },
    {
      "bootstrap_title_heading": "When to Fine-Tune a Model",
      "bootstrap_title_desc": "Fine-tuning is useful when the new dataset is large and similar to the original dataset used for pretraining."
    },
    {
      "bootstrap_title_heading": "Common Mistakes in Transfer Learning",
      "bootstrap_title_desc": "- Training all layers at once\n- Using high learning rates\n- Not resizing images correctly\n- Forgetting to normalize input data"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Medical image diagnosis\n- Face recognition systems\n- Wildlife species classification\n- Industrial defect detection"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Load a pretrained CNN model.\n2. Freeze base layers.\n3. Add custom classification layers.\n4. Train and evaluate the model.\n\n**Goal**: Learn how to reuse powerful CNN models for new tasks."
    }
  ]
},

{
  "id": 26,
  "html_title": "pretrained-cnn-architectures",
  "coursename": "Pretrained CNN Architectures",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What are CNN Architectures?",
      "bootstrap_title_desc": "CNN architectures define the structure and design of convolutional neural networks, including how layers are arranged and connected. Over time, many powerful architectures have been developed to improve accuracy and efficiency."
    },
    {
      "bootstrap_title_heading": "Why Pretrained Architectures Matter",
      "bootstrap_title_desc": "- Proven performance on large datasets\n- Faster development\n- Better generalization\n- Industry-standard solutions"
    },
    {
      "bootstrap_title_heading": "LeNet",
      "bootstrap_title_desc": "LeNet is one of the earliest CNN architectures, designed for handwritten digit recognition. It introduced convolution, pooling, and fully connected layers."
    },
    {
      "bootstrap_title_heading": "AlexNet",
      "bootstrap_title_desc": "AlexNet popularized deep learning for computer vision by winning the ImageNet competition in 2012. It uses ReLU activation and dropout to improve performance."
    },
    {
      "bootstrap_title_heading": "VGGNet (VGG16 / VGG19)",
      "bootstrap_title_desc": "VGG networks use very small (3×3) convolution filters stacked deep. They are simple and effective but computationally heavy."
    },
    {
      "bootstrap_title_heading": "GoogLeNet (Inception)",
      "bootstrap_title_desc": "GoogLeNet introduced the Inception module, which processes multiple filter sizes in parallel to capture features at different scales."
    },
    {
      "bootstrap_title_heading": "ResNet",
      "bootstrap_title_desc": "ResNet uses residual connections (skip connections) to allow very deep networks to train effectively, solving the vanishing gradient problem."
    },
    {
      "bootstrap_title_heading": "MobileNet",
      "bootstrap_title_desc": "MobileNet is designed for mobile and edge devices. It uses depthwise separable convolutions to reduce computation and model size."
    },
    {
      "bootstrap_title_heading": "EfficientNet",
      "bootstrap_title_desc": "EfficientNet scales network depth, width, and resolution in a balanced way, achieving high accuracy with fewer parameters."
    },
    {
      "bootstrap_title_heading": "Choosing the Right Architecture",
      "bootstrap_title_desc": "- Small dataset → Transfer learning (VGG, ResNet)\n- Mobile deployment → MobileNet\n- High accuracy → EfficientNet or ResNet"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Compare different CNN architectures.\n2. Identify their strengths and weaknesses.\n3. Choose a suitable model for a given use case.\n\n**Goal**: Understand popular CNN architectures used in real-world applications."
    }
  ]
},


{
  "id": 27,
  "html_title": "object-detection-basics",
  "coursename": "Object Detection Basics",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Object Detection?",
      "bootstrap_title_desc": "Object detection is a computer vision task that identifies and locates objects in images or videos. Unlike image classification, object detection not only predicts the class of an object but also draws a bounding box around it."
    },
    {
      "bootstrap_title_heading": "Image Classification vs Object Detection",
      "bootstrap_title_desc": "- Image classification predicts a single label for the entire image\n- Object detection finds multiple objects and their locations\n- Output includes class label and bounding box coordinates"
    },
    {
      "bootstrap_title_heading": "Bounding Boxes",
      "bootstrap_title_desc": "Bounding boxes are rectangles drawn around detected objects. They are represented using coordinates (x, y, width, height) or (x1, y1, x2, y2)."
    },
    {
      "bootstrap_title_heading": "Traditional Object Detection (Haar & HOG)",
      "bootstrap_title_desc": "Early object detection methods used hand-crafted features such as Haar features and HOG (Histogram of Oriented Gradients). These methods are limited compared to deep learning-based approaches."
    },
    {
      "bootstrap_title_heading": "Deep Learning-Based Object Detection",
      "bootstrap_title_desc": "Modern object detection uses deep learning models that learn features automatically. These models are faster and more accurate than traditional methods."
    },
    {
      "bootstrap_title_heading": "Popular Object Detection Models",
      "bootstrap_title_desc": "- YOLO (You Only Look Once)\n- SSD (Single Shot Detector)\n- Faster R-CNN\n\nThese models differ in speed and accuracy."
    },
    {
      "bootstrap_title_heading": "Object Detection Pipeline",
      "bootstrap_title_desc": "Input Image → Feature Extraction → Bounding Box Prediction → Class Prediction → Output"
    },
    {
      "bootstrap_title_heading": "Example: Object Detection Concept",
      "bootstrap_title_desc": "An object detection model can identify multiple objects in an image, such as detecting cars, people, and traffic signs in a single frame."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Autonomous driving\n- Surveillance systems\n- Retail product detection\n- Traffic monitoring"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Identify objects and bounding boxes in sample images.\n2. Compare object detection with image classification.\n3. Research YOLO, SSD, and Faster R-CNN.\n\n**Goal**: Understand how object detection works and where it is used."
    }
  ]
},

{
  "id": 28,
  "html_title": "image-segmentation",
  "coursename": "Image Segmentation Basics",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Image Segmentation?",
      "bootstrap_title_desc": "Image segmentation is the process of dividing an image into meaningful regions or segments. Unlike object detection, segmentation assigns a label to every pixel, helping the computer understand exactly which pixels belong to which object."
    },
    {
      "bootstrap_title_heading": "Why Image Segmentation is Important",
      "bootstrap_title_desc": "- Precise object boundaries\n- Pixel-level understanding\n- Better performance in medical and autonomous systems\n- Essential for advanced computer vision tasks"
    },
    {
      "bootstrap_title_heading": "Types of Image Segmentation",
      "bootstrap_title_desc": "- **Semantic Segmentation**: Labels each pixel by class (e.g., road, car)\n- **Instance Segmentation**: Separates different instances of the same class\n- **Panoptic Segmentation**: Combines semantic and instance segmentation"
    },
    {
      "bootstrap_title_heading": "Traditional Segmentation Techniques",
      "bootstrap_title_desc": "- Thresholding-based segmentation\n- Edge-based segmentation\n- Region-based segmentation\n- Watershed algorithm"
    },
    {
      "bootstrap_title_heading": "Example: Watershed Segmentation Concept",
      "bootstrap_title_desc": "Watershed treats the image like a topographic map and separates regions based on intensity differences. It is commonly used for separating overlapping objects."
    },
    {
      "bootstrap_title_heading": "Deep Learning-Based Segmentation",
      "bootstrap_title_desc": "Modern segmentation uses deep learning models that learn pixel-level features automatically, providing higher accuracy than traditional methods."
    },
    {
      "bootstrap_title_heading": "Popular Segmentation Models",
      "bootstrap_title_desc": "- U-Net (medical imaging)\n- Fully Convolutional Networks (FCN)\n- Mask R-CNN\n- DeepLab"
    },
    {
      "bootstrap_title_heading": "Segmentation vs Object Detection",
      "bootstrap_title_desc": "Object detection outputs bounding boxes, while segmentation provides exact object shapes by labeling every pixel."
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Medical image analysis (tumor detection)\n- Autonomous driving (road and lane segmentation)\n- Satellite image analysis\n- Background removal"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Identify examples of segmentation in real-world apps.\n2. Compare segmentation with object detection.\n3. Explore U-Net and Mask R-CNN architectures.\n\n**Goal**: Understand pixel-level image understanding using segmentation techniques."
    }
  ]
},

{
  "id": 29,
  "html_title": "model-deployment-basics",
  "coursename": "Model Deployment Basics for Computer Vision",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is Model Deployment?",
      "bootstrap_title_desc": "Model deployment is the process of making a trained machine learning or deep learning model available for real-world use. In computer vision, this means using a trained model to make predictions on new images or videos outside the training environment."
    },
    {
      "bootstrap_title_heading": "Why Deployment is Important",
      "bootstrap_title_desc": "- Turns models into real applications\n- Enables user interaction\n- Allows real-time predictions\n- Bridges the gap between research and production"
    },
    {
      "bootstrap_title_heading": "Common Deployment Options",
      "bootstrap_title_desc": "- Web applications\n- REST APIs\n- Mobile applications\n- Edge devices (IoT, cameras)"
    },
    {
      "bootstrap_title_heading": "Basic Deployment Pipeline",
      "bootstrap_title_desc": "Train Model → Save Model → Load Model → Accept Input → Return Prediction"
    },
    {
      "bootstrap_title_heading": "Saving and Loading Models",
      "bootstrap_title_desc": "Deep learning frameworks allow saving trained models for later use:\n\n```python\nmodel.save(\"model.h5\")\n```\n\n```python\nfrom tensorflow.keras.models import load_model\nmodel = load_model(\"model.h5\")\n```"
    },
    {
      "bootstrap_title_heading": "Using Models for Inference",
      "bootstrap_title_desc": "Inference means using the trained model to make predictions on new data such as images uploaded by users."
    },
    {
      "bootstrap_title_heading": "Example: Image Prediction Workflow",
      "bootstrap_title_desc": "Load image → Resize → Normalize → Model Prediction → Display Result"
    },
    {
      "bootstrap_title_heading": "Deployment Tools and Frameworks",
      "bootstrap_title_desc": "- Flask / FastAPI (backend APIs)\n- Streamlit (quick ML apps)\n- Docker (containerization)\n- Cloud platforms (AWS, GCP)"
    },
    {
      "bootstrap_title_heading": "Challenges in Model Deployment",
      "bootstrap_title_desc": "- Performance and latency\n- Model size optimization\n- Hardware limitations\n- Security and scalability"
    },
    {
      "bootstrap_title_heading": "Real-World Applications",
      "bootstrap_title_desc": "- Face recognition apps\n- Medical diagnosis systems\n- Smart surveillance\n- AI-powered mobile apps"
    },
    {
      "bootstrap_title_heading": "Exercise",
      "bootstrap_title_desc": "1. Save a trained CNN model.\n2. Load it in a new script.\n3. Perform inference on a test image.\n4. Think about how you would deploy it as a web app.\n\n**Goal**: Understand how to move computer vision models from development to real-world use."
    }
  ]
},


{
  "id": 30,
  "html_title": "final-project",
  "coursename": "Final Project",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Course Recap",
      "bootstrap_title_desc": "Over the last 30 days, you learned the complete computer vision pipeline—from image basics and OpenCV to deep learning, CNNs, object detection, segmentation, and deployment. This foundation prepares you for real-world computer vision applications."
    },
    {
      "bootstrap_title_heading": "Purpose of the Final Project",
      "bootstrap_title_desc": "The final project helps you apply everything you have learned in a single, end-to-end computer vision application. It demonstrates your understanding of data preprocessing, model building, evaluation, and deployment."
    },
    {
      "bootstrap_title_heading": "Suggested Final Project Ideas",
      "bootstrap_title_desc": "- Image classification system (cats vs dogs)\n- Face detection and recognition system\n- Real-time object detection using webcam\n- Mask detection system\n- Handwritten digit recognition\n- Medical image classification demo"
    },
    {
      "bootstrap_title_heading": "Final Project Workflow",
      "bootstrap_title_desc": "1. Define the problem\n2. Collect and preprocess data\n3. Build or select a CNN model\n4. Train and evaluate the model\n5. Test with new images or video\n6. Deploy or demo the application"
    },
    {
      "bootstrap_title_heading": "Example: Simple Project Pipeline",
      "bootstrap_title_desc": "Dataset → Preprocessing → CNN Model → Training → Evaluation → Deployment"
    },
    {
      "bootstrap_title_heading": "Project Evaluation Criteria",
      "bootstrap_title_desc": "- Correct problem definition\n- Proper preprocessing\n- Model accuracy and evaluation\n- Code structure and clarity\n- Working demo or deployment"
    },
    {
      "bootstrap_title_heading": "Next Learning Paths",
      "bootstrap_title_desc": "- Advanced object detection (YOLO, SSD)\n- Advanced segmentation (U-Net, Mask R-CNN)\n- Video analytics\n- Edge AI and mobile deployment\n- Research and competitions (Kaggle)"
    },
    {
      "bootstrap_title_heading": "Career Opportunities",
      "bootstrap_title_desc": "- Computer Vision Engineer\n- AI / ML Engineer\n- Data Scientist\n- Research Engineer\n- Robotics Engineer"
    },
    {
      "bootstrap_title_heading": "Congratulations!",
      "bootstrap_title_desc": "You have successfully completed a 30-day Computer Vision learning journey. Keep practicing, build more projects, and stay updated with the latest advancements in AI and computer vision."
    },
    {
      "bootstrap_title_heading": "Task",
      "bootstrap_title_desc": "Choose one final project idea, implement it end-to-end, and create a short demo or presentation explaining your approach, model, and results.\n\n**Goal**: Transition from learning to building real-world computer vision solutions."
    }
  ]
}







































 
  
  
    ];
    export default Computer_Vision;
    